import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Trash2, Eye, Power, PowerOff, Filter, X, Save, AlertCircle, AlertTriangle } from 'lucide-react';
import Pagination from './Pagination';
import validationService from './services/validationService';
import { api } from './services/apiClient';
import { logger } from './services/logger';

// Mock templates para el selector
const mockTemplates = [
  { id: 1, name: 'Plantilla Catastral' },
  { id: 2, name: 'Plantilla Vial' },
  { id: 3, name: 'Plantilla Ambiental' }
];

// Mock features para el autocompletado
const mockFeatures = [
  { id: 1, name: 'Transformadores', geometryType: 'Point' },
  { id: 2, name: 'Estructuras de soporte', geometryType: 'Point' },
  { id: 3, name: 'Proyectos', geometryType: 'Polygon' },
  { id: 4, name: 'Red Vial', geometryType: 'Line' }
];

// Mock de campos por feature para el autocompletado dependiente
const mockFieldsByFeature = {
  'Transformadores': ['ID_TRANSFORMADOR', 'POTENCIA_KVA', 'TIPO_ACEITE', 'FECHA_INSTALACION', 'UBICACION_GEO'],
  'Estructuras de soporte': ['ID_ESTRUCTURA', 'TIPO_MATERIAL', 'ALTURA', 'ESTADO_CONSERVACION', 'FECHA_INSPECCION'],
  'Proyectos': ['ID_PROYECTO', 'NOMBRE_PROYECTO', 'FECHA_INICIO', 'RESPONSABLE', 'PRESUPUESTO'],
  'Red Vial': ['ID_VIA', 'TIPO_VIA', 'LONGITUD_KM', 'ESTADO_VIA', 'NOMBRE_VIA']
};

// Tipos de validación disponibles (ahora se cargan dinámicamente desde el backend)

// Operadores por tipo de validación
const operatorsByType = {
  syntactic: [
    { value: 'required', label: 'Campo Requerido' },
    { value: 'not_blank', label: 'Campo no vacío' },
    { value: 'max_length', label: 'Longitud Máxima' },
    { value: 'min_length', label: 'Longitud Mínima' },
    { value: 'exact_length', label: 'Longitud Exacta' },
    { value: 'regex', label: 'Expresión Regular' },
    { value: 'data_type', label: 'Tipo de Dato' },
    { value: 'numeric_range', label: 'Rango Numérico' },
    { value: 'numeric_precision', label: 'Precisión Numérica' },
    { value: 'date_range', label: 'Rango de Fechas' },
    { value: 'value_not_equals', label: 'Valor no permitido' },
    { value: 'unique', label: 'Valor único' }
  ],
  topological: [
    { value: 'intersects', label: 'Intersecta' },
    { value: 'contains', label: 'Contiene' },
    { value: 'within', label: 'Dentro de' },
    { value: 'overlaps', label: 'Superposición' },
    { value: 'min_area', label: 'Área Mínima' },
    { value: 'max_area', label: 'Área Máxima' },
    { value: 'continuity', label: 'Continuidad de líneas' }
  ],
  domain: [
    { value: 'in_domain', label: 'En Dominio' },
    { value: 'range', label: 'Rango de Valores' }
  ],
  relational: [
    { value: 'foreign_key', label: 'Clave Foránea' },
    { value: 'value_exists_in', label: 'Valor existe en' }
  ],
  structure: [
    { value: 'feature_required', label: 'La capa o tabla debe tener datos' },
    { value: 'table_integrity', label: 'Los features tienen que ser iguales' }
  ]
};

// Mock data para subtipos de dominio y dominios dependientes
const domainSubtypes = [
  { value: 'numeric_range', label: 'Rango Numérico' },
  { value: 'coded_list', label: 'Lista de Códigos' },
  { value: 'date_range', label: 'Rango de Fechas' }
];

const domainsBySubtype = {
  numeric_range: [
      { value: 'ALTURA_POSTE', label: 'Dominio de Altura de Poste (8-15m)' },
      { value: 'PRESION_RED', label: 'Dominio de Presión de Red (20-60 PSI)' }
  ],
  coded_list: [
      { value: 'ESTADO_CONSERVACION', label: 'Dominio de Estado de Conservación (Bueno, Regular, Malo)' },
      { value: 'TIPO_MATERIAL', label: 'Dominio de Tipo de Material (Concreto, Madera, Metal)' }
  ],
  date_range: [
      { value: 'FECHA_INSPECCION', label: 'Dominio de Fechas de Inspección (Últimos 5 años)' }
  ]
};

const topologicalOperatorsByGeometryType = {
  'Point': [
    { value: 'must_coincide_with', label: 'Must coincide with' },
    { value: 'must_be_covered_by_endpoint_of', label: 'Must be covered by endpoint of' },
    { value: 'point_must_be_properly_inside', label: 'Point must be properly inside' },
    { value: 'must_be_disjoint', label: 'Must be disjoint' },
    { value: 'point_must_be_covered_by_line', label: 'Point must be covered by line' },
    { value: 'must_be_covered_by_boundary_of', label: 'Must be covered by boundary of' }
  ],
  'Line': [
    { value: 'must_not_have_dangles', label: 'Must not have dangles' },
    { value: 'must_not_overlap', label: 'Must not overlap' },
    { value: 'must_not_intersect_with', label: 'Must not intersect with' },
    { value: 'must_not_intersect_or_touch_interior', label: 'Must not intersect or touch interior' },
    { value: 'must_not_intersect_or_touch_interior_with', label: 'Must not intersect or touch interior with' },
    { value: 'must_not_overlap_with', label: 'Must not overlap with' },
    { value: 'must_not_have_pseudo_nodes', label: 'Must not have pseudo nodes' },
    { value: 'must_not_self_overlap', label: 'Must not self-overlap' },
    { value: 'must_not_self_intersect', label: 'Must not self-intersect' },
    { value: 'must_be_single_part', label: 'Must be single part' },
    { value: 'must_be_covered_by_feature_class_of', label: 'Must be covered by feature class of' },
    { value: 'must_be_covered_by_boundary_of', label: 'Must be covered by boundary of' },
    { value: 'must_be_properly_inside', label: 'Must be properly inside' },
    { value: 'endpoint_must_be_covered_by', label: 'Endpoint must be covered by' }
  ],
  'Polygon': [
    { value: 'must_not_overlap', label: 'Must not overlap' },
    { value: 'contains_point', label: 'Contains point' },
    { value: 'must_be_covered_by_feature_class_of', label: 'Must be covered by feature class of' },
    { value: 'must_not_overlap_with', label: 'Must not overlap with' },
    { value: 'area_boundary_must_be_covered_by_boundary_of', label: 'Area boundary must be covered by boundary of' },
    { value: 'must_not_have_gaps', label: 'Must not have gaps' },
    { value: 'contains_one_point', label: 'Contains one point' },
    { value: 'boundary_must_be_covered_by', label: 'Boundary must be covered by' },
    { value: 'must_be_covered_by', label: 'Must be covered by' },
    { value: 'must_cover_each_other', label: 'Must cover each other' }
  ]
};

// Función para obtener operadores hardcoded como fallback
const getHardcodedOperators = (type) => {
  const hardcodedOperators = {
    syntactic: [
      { value: 'required', label: 'Campo Requerido' },
      { value: 'not_blank', label: 'Campo no vacío' },
      { value: 'max_length', label: 'Longitud Máxima' },
      { value: 'min_length', label: 'Longitud Mínima' },
      { value: 'exact_length', label: 'Longitud Exacta' },
      { value: 'regex', label: 'Expresión Regular' },
      { value: 'data_type', label: 'Tipo de Dato' },
      { value: 'numeric_range', label: 'Rango Numérico' },
      { value: 'numeric_precision', label: 'Precisión Numérica' },
      { value: 'date_range', label: 'Rango de Fechas' },
      { value: 'value_not_equals', label: 'Valor no permitido' },
      { value: 'unique', label: 'Valor único' }
    ],
    topological: [
      { value: 'must_coincide_with', label: 'Must coincide with' },
      { value: 'must_be_covered_by_endpoint_of', label: 'Must be covered by endpoint of' },
      { value: 'point_must_be_properly_inside', label: 'Point must be properly inside' },
      { value: 'must_be_disjoint', label: 'Must be disjoint' },
      { value: 'point_must_be_covered_by_line', label: 'Point must be covered by line' },
      { value: 'must_be_covered_by_boundary_of', label: 'Must be covered by boundary of' },
      { value: 'continuity_lines', label: 'Continuidad de líneas' },
      { value: 'must_not_have_dangles', label: 'Must not have dangles' },
      { value: 'line_must_not_overlap', label: 'Line must not overlap' },
      { value: 'must_not_intersect', label: 'Must not intersect' },
      { value: 'must_not_intersect_with', label: 'Must not intersect with' },
      { value: 'must_not_intersect_or_touch_interior', label: 'Must not intersect or touch interior' },
      { value: 'must_not_intersect_or_touch_interior_with', label: 'Must not intersect or touch interior with' },
      { value: 'line_must_not_overlap_with', label: 'Line must not overlap with' },
      { value: 'must_not_have_pseudo_nodes', label: 'Must not have pseudo nodes' },
      { value: 'must_not_self_overlap', label: 'Must not self-overlap' },
      { value: 'must_not_self_intersect', label: 'Must not self-intersect' },
      { value: 'must_be_single_part', label: 'Must be single part' },
      { value: 'line_must_be_covered_by_feature_class_of', label: 'Line must be covered by feature class of' },
      { value: 'line_must_be_covered_by_boundary_of', label: 'Line must be covered by boundary of' },
      { value: 'line_must_be_properly_inside', label: 'Line must be properly inside' },
      { value: 'endpoint_must_be_covered_by', label: 'Endpoint must be covered by' },
      { value: 'polygon_must_not_overlap', label: 'Polygon must not overlap' },
      { value: 'contains_point', label: 'Contains point' },
      { value: 'polygon_must_be_covered_by_feature_class_of', label: 'Polygon must be covered by feature class of' },
      { value: 'polygon_must_not_overlap_with', label: 'Polygon must not overlap with' },
      { value: 'area_boundary_must_be_covered_by_boundary_of', label: 'Area boundary must be covered by boundary of' },
      { value: 'must_not_have_gaps', label: 'Must not have gaps' },
      { value: 'contains_one_point', label: 'Contains one point' },
      { value: 'boundary_must_be_covered_by', label: 'Boundary must be covered by' },
      { value: 'must_be_covered_by', label: 'Must be covered by' },
      { value: 'must_cover_each_other', label: 'Must cover each other' }
    ],
    domain: [
      { value: 'in_domain', label: 'En Dominio' },
      { value: 'range', label: 'Rango de Valores' }
    ],
    relational: [
      { value: 'foreign_key', label: 'Clave Foránea' },
      { value: 'value_exists_in', label: 'Valor existe en' }
    ],
    structure: [
      { value: 'feature_required', label: 'La capa o tabla debe tener datos' },
      { value: 'table_integrity', label: 'Los features tienen que ser iguales' }
    ]
  };
  return hardcodedOperators[type] || [];
};

// Cache de mappings cargados desde GET /api/metadata/geometry-types
// Se puebla en el primer mount del componente; mientras no esté disponible se usa el fallback hardcoded.
let _geometryTypeMappingsCache = null; // { [displayName]: baseType }

// Fallback hardcoded para cuando el API aún no ha respondido
const GEOMETRY_TYPE_FALLBACK = {
  'Point': 'Point', 'MultiPoint': 'Point',
  'Polyline': 'Line', 'MultiPolyline': 'Line',
  'LineString': 'Line', 'MultiLineString': 'Line',
  'LinearRing': 'Line', 'CircularString': 'Line',
  'CompoundCurve': 'Line', 'MultiCurve': 'Line', 'Curve': 'Line',
  'Polygon': 'Polygon', 'MultiPolygon': 'Polygon',
  'PolyhedralSurface': 'Polygon', 'MultiSurface': 'Polygon',
  'Surface': 'Polygon', 'CurvePolygon': 'Polygon',
  'GeometryCollection': 'Polygon', 'Geometry': 'Polygon'
};

// Función para mapear tipos de geometría complejos a tipos base.
// Usa el cache del API si está disponible; si no, usa el fallback hardcoded.
const mapGeometryType = (geometryType) => {
  if (!geometryType || geometryType === 'None' || geometryType.toLowerCase().includes('none')) return 'None';
  const lookup = _geometryTypeMappingsCache || GEOMETRY_TYPE_FALLBACK;
  if (lookup[geometryType]) return lookup[geometryType];
  if (geometryType.startsWith('Multi')) {
    const base = geometryType.substring(5);
    if (lookup[base]) return lookup[base];
  }
  logger.warn(`Tipo de geometría desconocido: ${geometryType}, usando Polygon como fallback`);
  return 'Polygon';
};


// Ícono de tipo de geometría al estilo ArcGIS Pro
const GeometryIcon = ({ geometryType, size = 14 }) => {
  const isTable = !geometryType || geometryType === 'None' || geometryType.toLowerCase().includes('none');
  const mapped = isTable ? null : mapGeometryType(geometryType);

  const svgProps = {
    width: size,
    height: size,
    viewBox: '0 0 16 16',
    style: { display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }
  };

  if (isTable || !mapped) {
    return (
      <svg {...svgProps}>
        <rect x="2" y="2" width="12" height="12" rx="1" fill="none" stroke="#9CA3AF" strokeWidth="1.5"/>
        <line x1="2" y1="6" x2="14" y2="6" stroke="#9CA3AF" strokeWidth="1.5"/>
        <line x1="8" y1="6" x2="8" y2="14" stroke="#9CA3AF" strokeWidth="1.5"/>
      </svg>
    );
  }
  if (mapped === 'Point') {
    return (
      <svg {...svgProps}>
        <circle cx="8" cy="8" r="4.5" fill="#3B82F6" stroke="#1D4ED8" strokeWidth="1"/>
      </svg>
    );
  }
  if (mapped === 'Line') {
    return (
      <svg {...svgProps}>
        <polyline points="2,14 6,6 10,10 14,2" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  // Polygon → Pentágono
  return (
    <svg {...svgProps}>
      <polygon points="8,2 13.7,6.1 11.5,12.9 4.5,12.9 2.3,6.1" fill="#10B981" stroke="#059669" strokeWidth="1"/>
    </svg>
  );
};

const ValidationRulesModule = ({ useAuth, usePermissions, useNotification, useLoading, BaseButton, BaseInput, BaseCard }) => {
  // Estado para mostrar opciones de autocompletado de plantilla
  const [showTemplateOptions, setShowTemplateOptions] = useState(false);
  const [showFeatureOptions, setShowFeatureOptions] = useState(false);
  const [showRelatedFeatureOptions, setShowRelatedFeatureOptions] = useState(false);
  const [showRelatedFieldOptions, setShowRelatedFieldOptions] = useState(false);
  const [showFieldOptions, setShowFieldOptions] = useState(false);
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { addNotification: showNotification } = useNotification();
  const { showLoading, hideLoading } = useLoading();

  // Estados principales
  const [validations, setValidations] = useState([]);
  const [filteredValidations, setFilteredValidations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [features, setFeatures] = useState([]); // Features globales (para metadata)
  const [templateFeatures, setTemplateFeatures] = useState([]); // Features de la plantilla seleccionada
  const [fieldsByFeature, setFieldsByFeature] = useState({});

  // Estados para indicadores de datos mock
  const [isUsingMockData, setIsUsingMockData] = useState(false);

  // Estados para datos dinámicos del backend
  const [validationTypes, setValidationTypes] = useState([]);
  const [validationCategories, setValidationCategories] = useState([]);
  const [validationSeverities, setValidationSeverities] = useState([]);
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [operatorMessages, setOperatorMessages] = useState({});
  const [operatorsByType, setOperatorsByType] = useState({}); // Operadores por tipo desde BD
  const [availableDomains, setAvailableDomains] = useState([]); // Dominios disponibles en la GDB
  const [showDomainTooltip, setShowDomainTooltip] = useState(false); // Control del tooltip de dominio
  const [tooltipDomain, setTooltipDomain] = useState(null); // Dominio para mostrar en tooltip

  // Estados de filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedTemplateFilter, setSelectedTemplateFilter] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Estados de modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedValidation, setSelectedValidation] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [validationToDelete, setValidationToDelete] = useState(null);

  // Estados del formulario
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'syntactic',
    templateId: '',
    featureName: '',
    fieldName: '',
    severity: 'error',
    operator: '',
    subtype: '',
    domain: '', // Campo separado para dominios
    relatedFeatureName: '',
    relatedFieldName: '',
    value: '',
    errorMessage: '',
    successMessage: ''
  });

  // Estados para trackear mensajes originales
  const [originalOperator, setOriginalOperator] = useState('');
  const [originalMessages, setOriginalMessages] = useState({ errorMessage: '', successMessage: '' });

  const [formErrors, setFormErrors] = useState({});

  // Función para cargar validaciones
  const loadValidations = async () => {
    setLoading(true);
    try {
      const data = await validationService.getValidations();
      // Ordenar por fecha de creación descendente (registros más nuevos primero)
      const sortedData = data.sort((a, b) => new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at));
      setValidations(sortedData);
      setFilteredValidations(sortedData);

      // Verificar si estamos usando datos mock
      const usingMock = sortedData && sortedData.length > 0 && sortedData.some(item => item._isMockData);
      setIsUsingMockData(usingMock);
      return sortedData;
    } catch (error) {
      showNotification('Error al cargar las validaciones', 'error');
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Cargar geometry-types desde API (una sola vez, popula el cache del módulo)
  useEffect(() => {
    if (_geometryTypeMappingsCache) return; // ya cargado por otra instancia
    api.get('/api/metadata/geometry-types')
      .then(res => {
        const mappings = res?.mappings || [];
        const lookup = {};
        mappings.forEach(m => { if (m.displayName && m.baseType) lookup[m.displayName] = m.baseType; });
        if (Object.keys(lookup).length > 0) _geometryTypeMappingsCache = lookup;
      })
      .catch(() => { /* silencioso — el fallback hardcoded se usa */ });
  }, []);

  // Efectos
  useEffect(() => {
    // Capturar ruleId síncronamente antes del await para que React StrictMode
    // (que ejecuta el efecto dos veces en dev) no pierda el valor al limpiar la URL
    const ruleId = new URLSearchParams(window.location.search).get('ruleId');
    if (ruleId) window.history.replaceState({}, '', '/?view=validations');

    const init = async () => {
      const data = await loadValidations();
      if (ruleId && data?.length) {
        const rule = data.find(v => v.id?.toString() === ruleId);
        if (rule) handleEdit(rule);
      }
    };
    init();
  }, []);

  // Cargar metadatos de validación desde el backend
  useEffect(() => {
    const loadValidationMetadata = async () => {
      setLoadingMetadata(true);
      try {
        const [types, categories, severities, templatesData] = await Promise.all([
          validationService.getValidationTypes(),
          validationService.getValidationCategories(),
          validationService.getValidationSeverities(),
          validationService.getTemplates()
        ]);

        // Convertir arrays simples a objetos con value/label/description
        const typesWithLabels = types.map(type => ({
          value: type,
          label: type.charAt(0).toUpperCase() + type.slice(1),
          description: getTypeDescription(type)
        }));

        const categoriesWithLabels = categories.map(category => ({
          value: category,
          label: category
        }));

        const severitiesWithLabels = severities.map(severity => ({
          value: severity,
          label: severity.charAt(0).toUpperCase() + severity.slice(1)
        }));

        // Convertir templates al formato esperado — filtrar por dueño si no es admin
        const allFormatted = templatesData.map(t => ({
          id: t.id,
          name: t.name,
          description: t.description,
          category: t.category,
          createdBy: t.createdBy,
          gdbStructureJson: t.gdbStructureJson
        }));
        const templatesFormatted = user?.role === 'admin'
          ? allFormatted
          : allFormatted.filter(t => t.createdBy === user?.username);

        setValidationTypes(typesWithLabels);
        setValidationCategories(categoriesWithLabels);
        setValidationSeverities(severitiesWithLabels);
        setTemplates(templatesFormatted);

        // Cargar operadores por tipo desde BD
        const operatorsData = {};
        for (const type of typesWithLabels) {
          try {
            const operators = await validationService.getOperatorsByType(type.value);
            // La API devuelve objetos completos, usarlos directamente
            operatorsData[type.value] = operators.map(op => ({
              value: op.value,
              label: op.label,
              geometryType: op.geometryType,
              geometryTypeFeatureWith: op.geometryTypeFeatureWith
            }));
          } catch (error) {
            logger.error(`Error loading operators for type ${type.value}:`, { error });
            // Fallback to hardcoded operators
            operatorsData[type.value] = getHardcodedOperators(type.value);
          }
        }
        setOperatorsByType(operatorsData);

        // Cargar mensajes por defecto para todos los tipos
        const messagesByType = {};
        for (const type of typesWithLabels) {
          try {
            const messages = await validationService.getOperatorDefaultMessages(type.value);
            messagesByType[type.value] = messages;
          } catch (error) {
            logger.error(`Error loading messages for type ${type.value}:`, { error });
            messagesByType[type.value] = {};
          }
        }
        setOperatorMessages(messagesByType);


      } catch (error) {
        logger.error('Error loading validation metadata:', { error });
        // Fallback to hardcoded data
        setValidationTypes([
          { value: 'syntactic', label: 'Sintáctica', description: 'Validación de tipos de datos, longitudes, formatos' },
          { value: 'topological', label: 'Topológica', description: 'Intersección, contención, proximidad, no solapamiento' },
          { value: 'domain', label: 'De Dominio', description: 'Cumplimiento de rangos o valores definidos' },
          { value: 'relational', label: 'Relacional', description: 'Existencia y coherencia de claves foráneas' },
          { value: 'structure', label: 'Estructura', description: 'La capa o tabla debe tener datos' }
        ]);
        setValidationCategories([
          { value: 'Datos', label: 'Datos' },
          { value: 'Geometría', label: 'Geometría' },
          { value: 'Dominio', label: 'Dominio' },
          { value: 'Relaciones', label: 'Relaciones' }
        ]);
        setValidationSeverities([
          { value: 'error', label: 'Error' },
          { value: 'warning', label: 'Advertencia' },
          { value: 'info', label: 'Información' }
        ]);
        setTemplates(mockTemplates);
        setFeatures(mockFeatures);
        setFieldsByFeature(mockFieldsByFeature);
      } finally {
        setLoadingMetadata(false);
      }
    };

    loadValidationMetadata();
  }, []);

  // Función auxiliar para obtener descripción de tipos
  const getTypeDescription = (type) => {
    const descriptions = {
      syntactic: 'Validación de tipos de datos, longitudes, formatos',
      topological: 'Intersección, contención, proximidad, no solapamiento',
      domain: 'Cumplimiento de rangos o valores definidos',
      relational: 'Existencia y coherencia de claves foráneas',
      structure: 'La capa o tabla debe tener datos'
    };
    return descriptions[type] || 'Tipo de validación personalizado';
  };
  useEffect(() => {
    applyFilters();
  }, [searchTerm, selectedType, selectedTemplate, selectedStatus, validations]);

  // Cargar mensajes por defecto cuando cambia el operador
  useEffect(() => {
    if (formData.operator && formData.type && operatorMessages[formData.type]?.[formData.operator]) {
      const defaultMessages = operatorMessages[formData.type][formData.operator];

      // Si el operador cambió al original de la validación, restaurar mensajes originales
      if (formData.operator === originalOperator && originalMessages.errorMessage) {
        setFormData(prev => ({
          ...prev,
          errorMessage: originalMessages.errorMessage,
          successMessage: originalMessages.successMessage
        }));
        // Limpiar errores si hay mensajes válidos
        if (originalMessages.errorMessage && originalMessages.successMessage) {
          setFormErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.errorMessage;
            delete newErrors.successMessage;
            return newErrors;
          });
        }
      }
      // Si cambió a un operador diferente, cargar mensajes por defecto
      else if (formData.operator !== originalOperator) {
        if (defaultMessages.errorMessage) {
          setFormData(prev => ({ ...prev, errorMessage: defaultMessages.errorMessage }));
        }
        if (defaultMessages.successMessage) {
          setFormData(prev => ({ ...prev, successMessage: defaultMessages.successMessage }));
        }
        // Limpiar errores si se cargaron mensajes por defecto
        if (defaultMessages.errorMessage && defaultMessages.successMessage) {
          setFormErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.errorMessage;
            delete newErrors.successMessage;
            return newErrors;
          });
        }
      }
    }
  }, [formData.operator, formData.type, operatorMessages, originalOperator, originalMessages]);

  // Limpiar tooltip cuando cambia el operador o el dominio seleccionado
  useEffect(() => {
    setShowDomainTooltip(false);
    setTooltipDomain(null);
  }, [formData.operator, formData.domain]);

  // Inicializar tooltip oculto al montar el componente
  useEffect(() => {
    setShowDomainTooltip(false);
    setTooltipDomain(null);
  }, []);

  // Cargar features de la plantilla seleccionada
  useEffect(() => {
    const loadTemplateFeatures = async () => {
      logger.debug(`loadTemplateFeatures: templateId = ${formData.templateId}`);
      if (formData.templateId && !isNaN(formData.templateId)) {
        try {
          logger.debug(`loadTemplateFeatures: Loading features for template ${formData.templateId}`);
          const structure = await validationService.getTemplateStructure(formData.templateId);
          logger.debug(`loadTemplateFeatures: Structure received`, { structure });

          // Combinar featureClasses y tables para tener todos los elementos disponibles
          const allItems = [
            ...(structure.featureClasses || []),
            ...(structure.tables || [])
          ];

          if (allItems.length > 0) {
            logger.debug(`loadTemplateFeatures: Found ${allItems.length} feature classes/tables`);
            // Convertir al formato esperado
            const featuresFromTemplate = allItems.map(fc => ({
              id: fc.id || fc.name, // Usar name como id si no hay id
              name: fc.name,
              geometryType: fc.type || fc.geometryType, // Algunos usan 'type', otros 'geometryType'
              description: fc.description || `Feature class ${fc.name}`
            }));

            logger.debug(`loadTemplateFeatures: Converted features`, { featuresFromTemplate });
            setTemplateFeatures(featuresFromTemplate);

            // Cargar campos para cada feature
            const fieldsByFeatureData = {};
            for (const feature of featuresFromTemplate) {
              const featureClass = allItems.find(fc => fc.name === feature.name);
              if (featureClass && featureClass.fields) {
                fieldsByFeatureData[feature.name] = featureClass.fields.map(f => f.name);
                logger.debug(`loadTemplateFeatures: Fields for ${feature.name}`, { fields: featureClass.fields.map(f => f.name) });
              } else {
                fieldsByFeatureData[feature.name] = [];
                logger.warn(`loadTemplateFeatures: No fields found for ${feature.name}`);
              }
            }
            setFieldsByFeature(fieldsByFeatureData);
            logger.debug(`loadTemplateFeatures: Fields loaded`, { fieldsByFeatureData });

            // Extraer dominios disponibles de la estructura GDB
            const domainsFromStructure = [];
            if (structure.domains && Array.isArray(structure.domains)) {
              structure.domains.forEach(domain => {
                domainsFromStructure.push({
                  id: domain.id || domain.name,
                  name: domain.name,
                  type: domain.type || 'CodedValue', // Tipo del dominio (CodedValue, Range, etc.)
                  description: domain.description || `Dominio ${domain.name}`,
                  values: domain.values || [], // Incluir valores del dominio para tooltip
                  minValue: domain.minValue, // Valor mínimo para rangos
                  maxValue: domain.maxValue // Valor máximo para rangos
                });
              });
              logger.debug(`loadTemplateFeatures: Found ${domainsFromStructure.length} domains`, { domains: domainsFromStructure.map(d => ({ name: d.name, type: d.type, valuesCount: d.values?.length || 0 })) });
            } else {
              logger.warn(`loadTemplateFeatures: No domains found in template structure`);
            }
            setAvailableDomains(domainsFromStructure);
          } else {
            logger.warn(`loadTemplateFeatures: No featureClasses or tables found in structure`);
            setTemplateFeatures([]);
            setFieldsByFeature({});
          }
        } catch (error) {
          logger.error('Error loading template features:', { error });
          setTemplateFeatures([]);
          setFieldsByFeature({});
          setAvailableDomains([]);
        }
      } else {
        logger.debug(`loadTemplateFeatures: No valid templateId (${formData.templateId})`);
        setTemplateFeatures([]);
        setFieldsByFeature({});
      }
    };

    loadTemplateFeatures();
  }, [formData.templateId]);

  // Lógica de paginación
  const itemsPerPage = 10;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredValidations.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredValidations.length / itemsPerPage);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    } else if (currentPage === 0 && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [filteredValidations, currentPage, totalPages]);

  // Función para aplicar filtros
  const applyFilters = () => {
    let filtered = validations;

    // Filtro por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(v => 
        v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro por tipo
    if (selectedType !== 'all') {
      filtered = filtered.filter(v => v.type === selectedType);
    }

    // Filtro por plantilla
    if (selectedTemplate && selectedTemplate !== 'all') {
      // Encontrar el ID de la plantilla seleccionada por nombre
      const selectedTemplateObj = templates.find(t => t.name === selectedTemplate);
      if (selectedTemplateObj) {
        filtered = filtered.filter(v => parseInt(v.templateId) === selectedTemplateObj.id);
      }
    }

    // Filtro por estado
    if (selectedStatus !== 'all') {
      const isActive = selectedStatus === 'active';
      filtered = filtered.filter(v => v.isActive === isActive);
    }

    setFilteredValidations(filtered);
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Función para limpiar filtros
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedType('all');
    setSelectedTemplate('');
    setSelectedTemplateFilter('all');
    setSelectedStatus('all');
  };

  // Función para validar formulario
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) errors.name = 'El nombre es requerido';
    // No validar templateId en modo edición (no es editable)
    if (!showEditModal && !formData.templateId) errors.templateId = 'Debe seleccionar una plantilla';
    if (!formData.featureName.trim()) errors.featureName = 'El feature es obligatorio';
    if (['syntactic', 'domain', 'relational'].includes(formData.type) && !formData.fieldName.trim()) errors.fieldName = 'El campo es requerido';
    if (!formData.operator) errors.operator = 'El operador es requerido';
    if (!formData.errorMessage.trim()) errors.errorMessage = 'El mensaje es requerido';

    // Validación de unicidad del nombre por plantilla
    if (formData.name.trim() && formData.templateId) {
      const existingValidation = validations.find(v =>
        v.name.toLowerCase() === formData.name.toLowerCase().trim() &&
        v.templateId === parseInt(formData.templateId) &&
        (!showEditModal || v.id !== selectedValidation?.id) // Excluir la validación actual en modo edición
      );
      if (existingValidation) {
        errors.name = 'Ya existe una validación con este nombre en la plantilla seleccionada';
      }
    }

    if (formData.type === 'domain') {
      // Para validaciones de dominio, el operador determina qué campo es requerido
      if (formData.operator === 'in_domain' && !formData.domain) {
        errors.domain = 'El dominio es requerido para validaciones "En Dominio"';
      }
      if (formData.operator === 'range' && !formData.domain) {
        errors.domain = 'El dominio es requerido para validaciones de rango';
      }
    }

    if (['foreign_key', 'value_exists_in'].includes(formData.operator)) {
      if (!formData.relatedFeatureName.trim()) errors.relatedFeatureName = 'La feature relacionada es requerida';
      if (!formData.relatedFieldName.trim()) errors.relatedFieldName = 'El campo relacionado es requerido';
    }

    // Operadores topológicos inter-capa: requieren feature relacionada (pero no campo relacionado)
    const topologyInterLayerOps = operatorsWithRelatedFeature.filter(
      op => !['foreign_key', 'value_exists_in', 'table_integrity', 'cardinality'].includes(op)
    );
    if (topologyInterLayerOps.includes(formData.operator) && !formData.relatedFeatureName?.trim()) {
      errors.relatedFeatureName = 'La feature relacionada es requerida para este operador';
    }

    // Validación específica por tipo
    if (operatorsToShowValueInput.includes(formData.operator) && !formData.value.trim()) {
      errors.value = 'El valor es requerido para este tipo de validación';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Función para resetear formulario y estado del modal
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'syntactic',
      templateId: '',
      featureName: '',
      fieldName: '',
      severity: 'error',
      operator: '',
      subtype: '',
      domain: '',
      relatedFeatureName: '',
      relatedFieldName: '',
      value: '',
      errorMessage: '',
      successMessage: ''
    });
    setFormErrors({});
    // Limpiar estado del modal
    setAvailableDomains([]);
    setTemplateFeatures([]);
    setFieldsByFeature({});
    setShowDomainTooltip(false);
    setTooltipDomain(null);
  };

  // Función para crear validación
  const handleCreate = async () => {
    if (!validateForm()) return;

    if (!hasPermission('validations', 'create')) {
      showNotification('No tienes permisos para crear validaciones', 'error');
      return;
    }

    // Validar que templateId sea un número válido
    const templateIdNum = parseInt(formData.templateId);
    if (isNaN(templateIdNum)) {
      logger.error('Invalid templateId:', { templateId: formData.templateId });
      showNotification('Error: Plantilla inválida seleccionada', 'error');
      return;
    }

    setLoading(true);
    showLoading('Creando validación...');
    try {
      const newValidationData = {
        ...formData,
        templateId: templateIdNum, // Asegurar que sea número válido
        templateName: templates.find(t => t.id === templateIdNum)?.name || '',
        createdBy: user.name,
        // Mapear nombres de campos relacionados al formato esperado por el backend
        featureNameRelated: formData.relatedFeatureName,
        fieldNameRelated: formData.relatedFieldName,
      };
      // Remover los campos con nombres incorrectos
      delete newValidationData.relatedFeatureName;
      delete newValidationData.relatedFieldName;

      await validationService.createValidation(newValidationData);
      showNotification('Validación creada exitosamente', 'success');
      setShowCreateModal(false);
      resetForm();
      // Recargar la lista de validaciones después de crear
      await loadValidations();
    } catch (error) {
      showNotification(error.message || 'Error al crear la validación', 'error');
    } finally {
      setLoading(false);
      hideLoading();
    }
  };

  // Función para editar validación
  const handleEdit = async (validation) => {
    if (!hasPermission('validations', 'edit')) {
      showNotification('No tienes permisos para editar validaciones', 'error');
      return;
    }

    setSelectedValidation(validation);

    // Guardar el operador y mensajes originales de la validación
    setOriginalOperator(validation.operator || '');
    setOriginalMessages({
      errorMessage: validation.errorMessage || '',
      successMessage: validation.successMessage || ''
    });

    // Cargar datos básicos de la validación
    const formDataToSet = {
      name: validation.name,
      description: validation.description || '',
      type: validation.type,
      templateId: validation.templateId.toString(),
      featureName: validation.featureName || '',
      fieldName: validation.fieldName || '',
      severity: validation.severity,
      operator: validation.operator || '',
      subtype: validation.subtype || '',
      domain: validation.domain || '',
      relatedFeatureName: validation.featureNameRelated || '',
      relatedFieldName: validation.fieldNameRelated || '',
      value: validation.value || '',
      errorMessage: validation.errorMessage || '',
      successMessage: validation.successMessage || ''
    };

    setFormData(formDataToSet);

    // Forzar recarga de estructura de plantilla
    setTimeout(() => {
      const loadTemplateStructure = async () => {
        if (validation.templateId) {
          try {
            logger.debug(`handleEdit: Loading template structure for ${validation.templateId}`);
            const structure = await validationService.getTemplateStructure(validation.templateId);
            logger.debug(`handleEdit: Structure loaded`, { structure });

            if (structure) {
              // Combinar featureClasses y tables para tener todos los elementos disponibles
              const allItems = [
                ...(structure.featureClasses || []),
                ...(structure.tables || [])
              ];

              const featuresFromTemplate = allItems.map(fc => ({
                id: fc.id || fc.name,
                name: fc.name,
                geometryType: fc.geometryType || fc.type,
                description: fc.description || `${fc.geometryType ? 'Feature class' : 'Tabla'} ${fc.name}`
              }));

              setTemplateFeatures(featuresFromTemplate);

              const fieldsByFeatureData = {};
              for (const feature of featuresFromTemplate) {
                const item = allItems.find(fc => fc.name === feature.name);
                if (item && item.fields) {
                  fieldsByFeatureData[feature.name] = item.fields.map(f => f.name);
                } else {
                  fieldsByFeatureData[feature.name] = [];
                }
              }
              setFieldsByFeature(fieldsByFeatureData);
              logger.debug(`handleEdit: Template structure loaded successfully with ${featuresFromTemplate.length} items`);
            } else {
              logger.warn(`handleEdit: No structure found for template`);
              setTemplateFeatures([]);
              setFieldsByFeature({});
            }
          } catch (error) {
            logger.error('Error loading template structure in edit:', { error });
            setTemplateFeatures([]);
            setFieldsByFeature({});
          }
        }
      };
      loadTemplateStructure();
    }, 100); // Pequeño delay para asegurar que el modal se abrió

    // Cargar mensajes después de un breve delay para asegurar que operatorMessages esté disponible
    setTimeout(() => {
      logger.debug(`handleEdit: Loading messages for validation`, {
        id: validation.id,
        type: validation.type,
        operator: validation.operator,
        currentErrorMessage: validation.errorMessage,
        currentSuccessMessage: validation.successMessage,
        operatorMessagesAvailable: !!operatorMessages[validation.type]?.[validation.operator]
      });

      const updatedFormData = { ...formDataToSet };

      // Si no hay mensaje personalizado, cargar mensaje por defecto
      if (validation.operator && validation.type && operatorMessages[validation.type]?.[validation.operator]) {
        const defaultMessages = operatorMessages[validation.type][validation.operator];
        logger.debug(`handleEdit: Default messages available`, { defaultMessages });

        if (!validation.errorMessage && defaultMessages.msg_error) {
          updatedFormData.errorMessage = defaultMessages.msg_error;
          logger.debug(`handleEdit: Set default error message: ${defaultMessages.msg_error}`);
        }
        if (!validation.successMessage && defaultMessages.msg_success) {
          updatedFormData.successMessage = defaultMessages.msg_success;
          logger.debug(`handleEdit: Set default success message: ${defaultMessages.msg_success}`);
        }
      } else {
        logger.warn(`handleEdit: No default messages available for ${validation.type}/${validation.operator}`);
      }

      // Si hay cambios, actualizar el estado
      if (updatedFormData.errorMessage !== formDataToSet.errorMessage || updatedFormData.successMessage !== formDataToSet.successMessage) {
        logger.debug(`handleEdit: Updating formData with messages`, {
          errorMessage: updatedFormData.errorMessage,
          successMessage: updatedFormData.successMessage
        });
        setFormData(updatedFormData);
      } else {
        logger.debug(`handleEdit: No message updates needed`);
      }
    }, 500); // Dar tiempo para que operatorMessages se cargue

    setShowEditModal(true);
  };

  // Función para actualizar validación
  const handleUpdate = async () => {
    if (!validateForm()) return;

    // Validar que templateId sea un número válido
    const templateIdNum = parseInt(formData.templateId);
    if (isNaN(templateIdNum)) {
      logger.error('Invalid templateId:', { templateId: formData.templateId });
      showNotification('Error: Plantilla inválida seleccionada', 'error');
      return;
    }

    setLoading(true);
    showLoading('Actualizando validación...');
    try {
      const updateData = {
        ...formData,
        templateId: templateIdNum, // Asegurar que sea número válido
        templateName: templates.find(t => t.id === templateIdNum)?.name || '',
        // Mapear nombres de campos relacionados al formato esperado por el backend
        featureNameRelated: formData.relatedFeatureName,
        fieldNameRelated: formData.relatedFieldName,
      };
      // Remover los campos con nombres incorrectos
      delete updateData.relatedFeatureName;
      delete updateData.relatedFieldName;

      await validationService.updateValidation(selectedValidation.id, updateData);
      showNotification('Validación actualizada exitosamente', 'success');
      setShowEditModal(false);
      setSelectedValidation(null);
      resetForm();
      // Recargar la lista de validaciones después de actualizar
      await loadValidations();
    } catch (error) {
      showNotification('Error al actualizar la validación', 'error');
    } finally {
      setLoading(false);
      hideLoading();
    }
  };

  // Función para eliminar validación
  const handleDelete = (validation) => {
    if (!hasPermission('validations', 'delete')) {
      showNotification('No tienes permisos para eliminar validaciones', 'error');
      return;
    }
    setValidationToDelete(validation);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!validationToDelete) return;

    setLoading(true);
    showLoading('Eliminando validación...');
    try {
      await validationService.deleteValidation(validationToDelete.id);
      showNotification('Validación eliminada exitosamente', 'success');
      setShowDeleteModal(false);
      setValidationToDelete(null);
      // Recargar la lista de validaciones después de eliminar
      await loadValidations();
    } catch (error) {
      showNotification('Error al eliminar la validación', 'error');
    } finally {
      setLoading(false);
      hideLoading();
    }
  };

  // Función para activar/desactivar validación
  const handleToggleActive = async (id) => {
    if (!hasPermission('validations', 'edit')) {
      showNotification('No tienes permisos para modificar validaciones', 'error');
      return;
    }

    setLoading(true);
    showLoading('Actualizando estado de validación...');
    try {
      const validation = validations.find(v => v.id === id);
      const newActiveState = !validation.isActive;

      // Llamar al backend para actualizar
      await validationService.toggleValidationActive(id);

      // Actualizar estado local
      const updatedValidations = validations.map(v =>
        v.id === id ? { ...v, isActive: newActiveState } : v
      );

      setValidations(updatedValidations);
      const action = newActiveState ? 'activada' : 'desactivada';
      showNotification(`Validación ${action} exitosamente`, 'success');

      // Recargar validaciones para asegurar sincronización
      await loadValidations();
    } catch (error) {
      showNotification('Error al cambiar el estado de la validación', 'error');
    } finally {
      setLoading(false);
      hideLoading();
    }
  };

  // Función para ver detalles
  const handleView = (validation) => {
    setSelectedValidation(validation);
    setShowViewModal(true);
  };

  // Función para obtener el color de la severidad
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error': return 'text-red-600 bg-red-50';
      case 'warning': return 'text-yellow-600 bg-yellow-50';
      case 'info': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  // Función para obtener el color del tipo
  const getTypeColor = (type) => {
    switch (type) {
      case 'syntactic': return 'text-blue-700 bg-blue-100';
      case 'topological': return 'text-green-700 bg-green-100';
      case 'domain': return 'text-purple-700 bg-purple-100';
      case 'relational': return 'text-primary-text bg-primary/10';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  // Extrae la info de referencia espacial de una feature desde el GdbStructureJson de la plantilla
  const getSpatialRefInfo = (templateId, featureName) => {
    const template = templates.find(t => t.id === parseInt(templateId));
    if (!template?.gdbStructureJson) return null;
    try {
      const structure = JSON.parse(template.gdbStructureJson);
      const fc = structure.featureClasses?.find(
        f => f.name?.toLowerCase() === featureName?.toLowerCase()
      );
      return fc?.spatialReferenceInfo || null;
    } catch {
      return null;
    }
  };

  // Operadores que requieren que el campo 'Valor' sea visible
  const operatorsToShowValueInput = [
    'max_length', 'min_length', 'exact_length', 'regex', 'data_type',
    'numeric_precision', 'numeric_range', 'date_range', 'range',
    'geometry_type', 'cardinality', 'value_not_equals'
  ];

  // Texto de ayuda por operador para el campo Valor
  const operatorValueHelpText = {
    max_length:        'Número entero — ej: 50 para máximo 50 caracteres',
    min_length:        'Número entero — ej: 3 para mínimo 3 caracteres',
    exact_length:      'Número entero — ej: 5 para exactamente 5 caracteres (DANE depto: 2, municipio: 5)',
    regex:             'Expresión regular — ej: ^[A-Z]{2}[0-9]{3}$ o ^[0-9]{5}$',
    numeric_precision: 'Número entero — ej: 2 para máximo 2 decimales',
    numeric_range:     'Formato: min,max — deja vacío un extremo para rango abierto. Ej: 0,100 · 10, (≥ 10) · ,50 (≤ 50)',
    date_range:        'Formato: aaaa-MM-dd,aaaa-MM-dd — ej: 2020-01-01,2025-12-31',
    range:             'Formato: min,max — ej: 0,100',
    geometry_type:     'Tipo de geometría esperado — ej: Polygon, MultiLineString, Point',
    cardinality:       'Formato: min,max — ej: 1,5 para entre 1 y 5 registros relacionados',
    value_not_equals:  'Uno o varios valores prohibidos separados por coma — ej: N/A,Sin dato,0',
  };

  const operatorsWithRelatedFeature = [
    'must_coincide_with', 'must_be_covered_by_endpoint_of', 'point_must_be_properly_inside',
    'must_be_disjoint', 'point_must_be_covered_by_line', 'must_be_covered_by_boundary_of',
    'must_not_intersect_with', 'must_not_intersect_or_touch_interior_with',
    'line_must_not_overlap_with', 'line_must_be_covered_by_feature_class_of',
    'line_must_be_covered_by_boundary_of', 'line_must_be_properly_inside',
    'endpoint_must_be_covered_by', 'contains_point', 'polygon_must_be_covered_by_feature_class_of',
    'polygon_must_not_overlap_with', 'area_boundary_must_be_covered_by_boundary_of',
    'contains_one_point', 'boundary_must_be_covered_by', 'must_be_covered_by',
    'must_cover_each_other', 'table_integrity', 'foreign_key', 'value_exists_in', 'cardinality'
  ];

  // Opciones para el selector de tipo de dato
  const dataTypesForSelect = [
    "Short", "Long", "Float", "Double", "Date", "Text", "Blob", "Guid", "Raster"
  ];

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    logger.debug(`handleTypeChange: Changing type from ${formData.type} to ${newType}`);

    const newData = {
      ...formData,
      type: newType,
      operator: '',
      subtype: '',
      domain: '',
      value: '',
      relatedFeatureName: '',
      relatedFieldName: '',
      errorMessage: '',
      successMessage: ''
    };

    if (newType === 'feature') {
      newData.fieldName = ''; // Limpiar el campo cuando se selecciona 'feature'
    }

    logger.debug(`handleTypeChange: Setting new formData`, { newData });
    setFormData(newData);
  };

  const getTopologicalOperators = () => {
    if (formData.type !== 'topological' || !formData.featureName) {
      return operatorsByType.topological || [];
    }

    const selectedFeature = templateFeatures.find(f => f.name === formData.featureName);
    if (!selectedFeature || !selectedFeature.geometryType) {
      return operatorsByType.topological || [];
    }

    // Aplicar mapeo de geometría para convertir tipos complejos a tipos base
    const mappedGeometryType = mapGeometryType(selectedFeature.geometryType);
    logger.debug(`getTopologicalOperators: Mapped ${selectedFeature.geometryType} → ${mappedGeometryType}`);

    // Filtrar operadores por geometría usando datos dinámicos
    const topologicalOps = operatorsByType.topological || [];
    const filteredOps = topologicalOps.filter(op => {
      // Si el operador no tiene restricción de geometría, incluirlo
      if (!op.geometryType) return true;
      // Si coincide con la geometría del feature, incluirlo
      return op.geometryType.toLowerCase() === mappedGeometryType.toLowerCase();
    });

    logger.debug(`getTopologicalOperators: Filtered ${topologicalOps.length} → ${filteredOps.length} operators for geometry ${mappedGeometryType}`);
    return filteredOps;
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Validaciones</h1>
          <p className="text-gray-600">Cree y gestione reglas de validación para sus plantillas</p>
        </div>
        {hasPermission('validations', 'create') && (
          <BaseButton
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2"
          >
            <Plus size={20} />
            Nueva Validación
          </BaseButton>
        )}
      </div>

      {/* Indicador de datos mock */}
      {isUsingMockData && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Modo Desarrollo - Datos de Prueba
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  Se están mostrando datos de validaciones de ejemplo porque el backend no tiene datos reales o está configurado para usar datos de prueba.
                  Esta funcionalidad está habilitada en el archivo de configuración para facilitar el desarrollo.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <BaseCard className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <BaseInput
              placeholder="Buscar validaciones..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Todos los tipos</option>
            {validationTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>

          <div className="relative">
            <input
              type="text"
              value={selectedTemplate === 'all' ? '' : selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value || 'all')}
              onFocus={() => setShowTemplateOptions(true)}
              onBlur={() => setTimeout(() => setShowTemplateOptions(false), 150)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
              placeholder="Buscar por plantilla..."
              autoComplete="off"
            />
            {showTemplateOptions && (selectedTemplate !== 'all' || selectedTemplate === '') && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-auto">
                {templates
                  .filter(t =>
                    (selectedTemplate === 'all' || selectedTemplate === '')
                      ? true
                      : t.name.toLowerCase().includes(selectedTemplate.toLowerCase())
                  )
                  .map(t => (
                    <div
                      key={t.id}
                      className="px-3 py-2 cursor-pointer hover:bg-blue-100"
                      onMouseDown={() => setSelectedTemplate(t.name)}
                    >
                      {t.name}
                    </div>
                  ))}
                {templates.filter(t => (selectedTemplate === 'all' || selectedTemplate === '') ? true : t.name.toLowerCase().includes(selectedTemplate.toLowerCase())).length === 0 && (
                  <div className="px-3 py-2 text-gray-400">No hay coincidencias</div>
                )}
              </div>
            )}
          </div>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activas</option>
            <option value="inactive">Inactivas</option>
          </select>

          <BaseButton
            variant="outline"
            onClick={clearFilters}
            className="flex items-center gap-2"
          >
            <X size={16} />
            Limpiar
          </BaseButton>
        </div>
      </BaseCard>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <BaseCard className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Validaciones</p>
              <p className="text-2xl font-bold text-gray-900">{filteredValidations.length}</p>
            </div>
            <Filter className="h-8 w-8 text-blue-600" />
          </div>
        </BaseCard>

        <BaseCard className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Activas</p>
              <p className="text-2xl font-bold text-green-600">
                {filteredValidations.filter(v => v.isActive).length}
              </p>
            </div>
            <Power className="h-8 w-8 text-green-600" />
          </div>
        </BaseCard>

        <BaseCard className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Inactivas</p>
              <p className="text-2xl font-bold text-red-600">
                {filteredValidations.filter(v => !v.isActive).length}
              </p>
            </div>
            <PowerOff className="h-8 w-8 text-red-600" />
          </div>
        </BaseCard>

        <BaseCard className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Plantillas</p>
              <p className="text-2xl font-bold text-purple-600">
                {new Set(filteredValidations.map(v => v.templateId)).size}
              </p>
            </div>
            <AlertCircle className="h-8 w-8 text-purple-600" />
          </div>
        </BaseCard>
      </div>

      {/* Tabla de validaciones */}
      <BaseCard>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
        <>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Validación
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Operador
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plantilla
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Severidad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Creado por
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((validation) => (
                <tr key={validation.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {validation.name}
                      </div>
                      {/* <div className="text-sm text-gray-500 truncate max-w-xs">
                        {validation.description}
                      </div> */}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(validation.type)}`}>
                      {validationTypes.find(t => t.value === validation.type)?.label || validation.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {operatorsByType[validation.type]?.find(op => op.value === validation.operator)?.label || validation.operator}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {(() => {
                      const template = templates.find(t => t.id === parseInt(validation.templateId));
                      return template ? template.name : 'Plantilla no encontrada';
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(validation.severity)}`}>
                      {validation.severity.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      validation.isActive 
                        ? 'text-green-800 bg-green-100' 
                        : 'text-red-800 bg-red-100'
                    }`}>
                      {validation.isActive ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {validation.createdBy}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleView(validation)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Ver detalles"
                      >
                        <Eye size={16} />
                      </button>

                      {hasPermission('validations', 'edit') && (
                        <>
                          <button
                            onClick={() => handleEdit(validation)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>

                          <button
                            onClick={() => handleToggleActive(validation.id)}
                            className={validation.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}
                            title={validation.isActive ? 'Desactivar' : 'Activar'}
                          >
                            {validation.isActive ? <PowerOff size={16} /> : <Power size={16} />}
                          </button>
                        </>
                      )}

                      {hasPermission('validations', 'delete') && (
                        <button
                          onClick={() => handleDelete(validation)}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredValidations.length === 0 && (
            <div className="text-center py-8">
              <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No hay validaciones</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || selectedType !== 'all' || selectedTemplate || selectedTemplateFilter !== 'all' || selectedStatus !== 'all'
                  ? 'No se encontraron validaciones con los filtros aplicados.'
                  : 'Comience creando una nueva validación.'}
              </p>
            </div>
          )}
        </div>
        <div className="pt-4 border-t border-gray-200">
          {/* Paginación mejorada con navegación a primera/última página */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Mostrando {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredValidations.length)} de {filteredValidations.length} validaciones
            </div>
            <div className="flex items-center space-x-2">
              <BaseButton
                variant="outline"
                size="sm"
                onClick={() => paginate(1)}
                disabled={currentPage === 1}
                className="px-2 py-1"
              >
                « Primera
              </BaseButton>
              <BaseButton
                variant="outline"
                size="sm"
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-2 py-1"
              >
                ‹ Anterior
              </BaseButton>
              <span className="text-sm text-gray-700 px-2">
                Página {currentPage} de {totalPages}
              </span>
              <BaseButton
                variant="outline"
                size="sm"
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-2 py-1"
              >
                Siguiente ›
              </BaseButton>
              <BaseButton
                variant="outline"
                size="sm"
                onClick={() => paginate(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1"
              >
                Última »
              </BaseButton>
            </div>
          </div>
        </div>
        </>
        )}
      </BaseCard>

      {/* Modal Crear/Editar Validación */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {showCreateModal ? 'Nueva Validación' : 'Editar Validación'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Información básica */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre *
                  </label>
                  <BaseInput
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Nombre de la validación"
                    error={formErrors.name}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plantilla *
                  </label>
                  {showEditModal ? (
                    // En modo edición, mostrar solo el nombre de la plantilla (no editable)
                    <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                      {(() => {
                        if (!formData.templateId) return 'Plantilla no seleccionada';
                        const t = templates.find(t => t.id === parseInt(formData.templateId));
                        return t ? t.name : `Plantilla ID: ${formData.templateId}`;
                      })()}
                    </div>
                  ) : (
                    // En modo creación, mostrar dropdown editable
                    <div className="relative">
                      <input
                        type="text"
                        value={(() => {
                          if (!formData.templateId) return '';
                          const t = templates.find(t => t.id === parseInt(formData.templateId));
                          return t ? t.name : formData.templateId;
                        })()}
                        onChange={e => {
                          const val = e.target.value;
                          setFormData(f => ({
                            ...f,
                            templateId: val === '' ? '' : val // temporal, se ajusta al seleccionar
                          }));
                          setShowTemplateOptions(true);
                        }}
                        onFocus={() => setShowTemplateOptions(true)}
                        onBlur={() => setTimeout(() => setShowTemplateOptions(false), 150)}
                        className={`px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full ${formErrors.templateId ? 'border-red-300' : 'border-gray-300'}`}
                        placeholder="Buscar plantilla..."
                        autoComplete="off"
                      />
                      {showTemplateOptions && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-auto">
                          {templates
                            .filter(t =>
                              formData.templateId === '' || isNaN(formData.templateId)
                                ? t.name.toLowerCase().includes((formData.templateId || '').toLowerCase())
                                : true
                            )
                            .map(t => (
                              <div
                                key={t.id}
                                className="px-3 py-2 cursor-pointer hover:bg-blue-100"
                                onMouseDown={() => {
                                  setFormData(f => ({ ...f, templateId: t.id.toString() }));
                                  if (formErrors.templateId) {
                                    const newErrors = { ...formErrors };
                                    delete newErrors.templateId;
                                    setFormErrors(newErrors);
                                  }
                                  setShowTemplateOptions(false);
                                }}
                              >
                                {t.name}
                              </div>
                            ))}
                          {templates.filter(t => (formData.templateId === '' || isNaN(formData.templateId)) ? t.name.toLowerCase().includes((formData.templateId || '').toLowerCase()) : true).length === 0 && (
                            <div className="px-3 py-2 text-gray-400">No hay coincidencias</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {formErrors.templateId && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.templateId}</p>
                  )}
                  {showEditModal && (
                    <p className="mt-1 text-xs text-gray-500">
                      La plantilla no se puede cambiar en la edición
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Feature *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.featureName}
                      onChange={e => {
                        const newFeatureName = e.target.value;
                        setFormData(f => {
                          const newFeature = features.find(feat => feat.name === newFeatureName);
                          const currentOperator = f.operator;
                          const newData = { ...f };

                          if (f.type === 'topological' && newFeature && currentOperator) {
                            const mappedGeomType = mapGeometryType(newFeature.geometryType);
                            const topOps = operatorsByType.topological || getHardcodedOperators('topological');
                            const allowedOperators = topOps
                              .filter(op => !op.geometryType || op.geometryType.toLowerCase() === mappedGeomType.toLowerCase())
                              .map(op => op.value);
                            if (!allowedOperators.includes(currentOperator)) {
                              newData.operator = '';
                            }
                          }
                          return { ...newData, featureName: newFeatureName };
                        });
                        setShowFeatureOptions(true);
                      }}
                      onFocus={() => setShowFeatureOptions(true)}
                      onBlur={() => {
                        setTimeout(() => {
                          setShowFeatureOptions(false);
                          // Verificar si el valor es válido al perder el foco
                          const isValidFeature = templateFeatures.some(f => f.name === formData.featureName);
                          if (!isValidFeature) {
                            // Si no es válido, limpiar el campo y el campo dependiente "Campo"
                            setFormData(prev => ({ ...prev, featureName: '', fieldName: '' }));
                          }
                        }, 150);
                      }}
                      className={`px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full ${formErrors.featureName ? 'border-red-300' : 'border-gray-300'}`}
                      placeholder="Buscar feature..."
                      autoComplete="off"
                    />
                    {showFeatureOptions && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-auto">
                        {templateFeatures
                          .filter(f => {
                            const matchesSearch = f.name.toLowerCase().includes((formData.featureName || '').toLowerCase());
                            // Para validaciones topológicas, excluir tablas (sin geometría)
                            const isTable = !f.geometryType || f.geometryType === 'None' || f.geometryType.toLowerCase() === 'none';
                            if (formData.type === 'topological' && isTable) return false;
                            return matchesSearch;
                          })
                          .map(f => (
                            <div
                              key={f.id}
                              className="px-3 py-2 cursor-pointer hover:bg-blue-100"
                              onMouseDown={() => {
                                setFormData(prev => {
                                  const currentOperator = prev.operator;
                                  const newData = { ...prev, fieldName: '' };
                                  if (prev.type === 'topological' && currentOperator) {
                                    const mappedGeomType = mapGeometryType(f.geometryType);
                                    const topOps = operatorsByType.topological || getHardcodedOperators('topological');
                                    const allowedOperators = topOps
                                      .filter(op => !op.geometryType || op.geometryType.toLowerCase() === mappedGeomType.toLowerCase())
                                      .map(op => op.value);
                                    if (!allowedOperators.includes(currentOperator)) {
                                      newData.operator = '';
                                    }
                                  }
                                  return { ...newData, featureName: f.name };
                                });
                                if (formErrors.featureName) {
                                  const newErrors = { ...formErrors };
                                  delete newErrors.featureName;
                                  setFormErrors(newErrors);
                                }
                                setShowFeatureOptions(false);
                              }}
                            >
                              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <GeometryIcon geometryType={f.geometryType} />
                                {f.name}
                              </span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                  {formErrors.featureName && <p className="mt-1 text-sm text-red-600">{formErrors.featureName}</p>}
                </div>

                {['syntactic', 'domain', 'relational'].includes(formData.type) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Campo *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.fieldName}
                        onChange={e => {
                          setFormData(f => ({ ...f, fieldName: e.target.value }));
                          setShowFieldOptions(true);
                        }}
                        onFocus={() => setShowFieldOptions(true)}
                        onBlur={() => setTimeout(() => setShowFieldOptions(false), 150)}
                        className={`px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full ${formErrors.fieldName ? 'border-red-300' : 'border-gray-300'}`}
                        placeholder="Buscar campo..."
                        autoComplete="off"
                        disabled={!formData.featureName}
                      />
                      {showFieldOptions && formData.featureName && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-auto">
                          {(fieldsByFeature[formData.featureName] || [])
                            .filter(field =>
                              field.toLowerCase().includes((formData.fieldName || '').toLowerCase())
                            )
                            .map(field => (
                              <div
                                key={field}
                                className="px-3 py-2 cursor-pointer hover:bg-blue-100"
                                onMouseDown={() => {
                                  setFormData(prev => ({ ...prev, fieldName: field }));
                                  if (formErrors.fieldName) {
                                    const newErrors = { ...formErrors };
                                    delete newErrors.fieldName;
                                    setFormErrors(newErrors);
                                  }
                                  setShowFieldOptions(false);
                                }}
                              >
                                {field}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                    {formErrors.fieldName && <p className="mt-1 text-sm text-red-600">{formErrors.fieldName}</p>}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción 
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Descripción de la validación"
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    formErrors.description ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {/* {formErrors.description && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
                )} */}
              </div>

              {/* Regla de validación */}
              <div className="border-t pt-4">
                <h4 className="text-md font-medium text-gray-900 mb-3">Configuración de la Regla</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Validación *
                    </label>
                    <select
                      value={formData.type}
                      onChange={handleTypeChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {validationTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      {validationTypes.find(t => t.value === formData.type)?.description}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Severidad *
                    </label>
                    <select
                      value={formData.severity}
                      onChange={(e) => setFormData({...formData, severity: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {validationSeverities.map(severity => (
                        <option key={severity.value} value={severity.value}>
                          {severity.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.type === 'domain' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Subtipo *
                      </label>
                      <select
                        value={formData.subtype}
                        onChange={(e) => setFormData(prev => ({ ...prev, subtype: e.target.value, value: '' }))}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.subtype ? 'border-red-300' : 'border-gray-300'}`}
                      >
                        <option value="">Seleccionar subtipo</option>
                        {domainSubtypes.map(st => (
                          <option key={st.value} value={st.value}>{st.label}</option>
                        ))}
                      </select>
                      {formErrors.subtype && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.subtype}</p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Operador *
                    </label>
                    <select
                      value={formData.operator}
                      onChange={(e) => {
                        const newOperator = e.target.value;
                        setFormData(prev => {
                          const newData = { ...prev, operator: newOperator, errorMessage: '', successMessage: '' };

                          // Limpiar campos condicionales si el operador cambia
                          if (!operatorsToShowValueInput.includes(newOperator)) {
                            newData.value = '';
                          }
                          if (!operatorsWithRelatedFeature.includes(newOperator)) {
                            newData.relatedFeatureName = '';
                          } else if (prev.relatedFeatureName) {
                            // Si el operador cambia pero sigue usando feature relacionada,
                            // verificar que la feature seleccionada siga siendo compatible con el nuevo geometryTypeFeatureWith
                            const ops = operatorsByType[prev.type] || [];
                            const newOp = ops.find(op => op.value === newOperator);
                            const newGeoFilter = newOp?.geometryTypeFeatureWith;
                            if (newGeoFilter) {
                              const selectedFeature = templateFeatures.find(f => f.name === prev.relatedFeatureName);
                              const selectedGeoType = selectedFeature ? mapGeometryType(selectedFeature.geometryType) : null;
                              if (!selectedGeoType || selectedGeoType.toLowerCase() !== newGeoFilter.toLowerCase()) {
                                newData.relatedFeatureName = '';
                                newData.relatedFieldName = '';
                              }
                            }
                          }
                          if (!['foreign_key', 'value_exists_in'].includes(newOperator)) {
                            newData.relatedFieldName = '';
                          }

                          return newData;
                        });
                        // Limpiar error de operador si existe
                        if (formErrors.operator) {
                          const newErrors = { ...formErrors };
                          delete newErrors.operator;
                          setFormErrors(newErrors);
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        formErrors.operator ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Seleccionar operador</option>
                      {(formData.type === 'topological'
                        ? getTopologicalOperators()
                        : (operatorsByType[formData.type] || []))
                      .map(op => (
                        <option key={op.value} value={op.value}>{op.label}</option>
                      ))}
                    </select>
                    {formErrors.operator && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.operator}</p>
                    )}
                    {formData.type === 'topological' && formData.featureName && (
                      <p className="mt-1 text-xs text-gray-500">
                        Tipo de geometría: <strong>{templateFeatures.find(f => f.name === formData.featureName)?.geometryType || 'No definido'}</strong>
                        {(() => {
                          const feature = templateFeatures.find(f => f.name === formData.featureName);
                          const originalType = feature?.geometryType;
                          const mappedType = originalType ? mapGeometryType(originalType) : null;
                          if (originalType && mappedType && originalType !== mappedType) {
                            return ` (mapeado a: ${mappedType})`;
                          }
                          return '';
                        })()}
                      </p>
                    )}
                  </div>
                </div>

                {operatorsWithRelatedFeature.includes(formData.operator) && (
                  <div className="mt-4">
                    <div className={`grid gap-4 ${['foreign_key', 'value_exists_in'].includes(formData.operator) ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Feature relacionada *</label>
                        <div className="relative">
                          <input
                            type="text"
                            value={formData.relatedFeatureName}
                            onChange={e => {
                              setFormData(f => ({ ...f, relatedFeatureName: e.target.value, relatedFieldName: '' }));
                              setShowRelatedFeatureOptions(true);
                            }}
                            onFocus={() => setShowRelatedFeatureOptions(true)}
                            onBlur={() => setTimeout(() => setShowRelatedFeatureOptions(false), 150)}
                            className={`px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full ${formErrors.relatedFeatureName ? 'border-red-300' : 'border-gray-300'}`}
                            placeholder="Buscar feature relacionada..."
                            autoComplete="off"
                          />
                          {showRelatedFeatureOptions && (
                            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-auto">
                              {templateFeatures
                                .filter(f => {
                                  if (!f.name.toLowerCase().includes((formData.relatedFeatureName || '').toLowerCase())) return false;
                                  
                                  // Si es una validación relacional, no filtrar por tipo de geometría
                                  // Las llaves foráneas pueden apuntar a tablas o capas geográficas indistintamente
                                  if (formData.type === 'relational') return true;

                                  // Para otros tipos (topológicas), filtrar por tipo de geometría si el operador lo especifica
                                  const ops = operatorsByType[formData.type] || [];
                                  const currentOp = ops.find(op => op.value === formData.operator);
                                  const geoFilter = currentOp?.geometryTypeFeatureWith;
                                  if (!geoFilter) return true;
                                  const featureGeoType = mapGeometryType(f.geometryType);
                                  return featureGeoType.toLowerCase() === geoFilter.toLowerCase();
                                })
                                .map(f => (
                                  <div
                                    key={`related-${f.id}`}
                                    className="px-3 py-2 cursor-pointer hover:bg-blue-100"
                                    onMouseDown={() => {
                                      setFormData(prev => ({ ...prev, relatedFeatureName: f.name, relatedFieldName: '' }));
                                      setShowRelatedFeatureOptions(false);
                                    }}
                                  >
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <GeometryIcon geometryType={f.geometryType} />
                                      {f.name}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                        {formErrors.relatedFeatureName && <p className="mt-1 text-sm text-red-600">{formErrors.relatedFeatureName}</p>}
                      </div>

                      {['foreign_key', 'value_exists_in'].includes(formData.operator) && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Campo relacionado *</label>
                          <div className="relative">
                            <input
                              type="text"
                              value={formData.relatedFieldName}
                              onChange={e => {
                                setFormData(f => ({ ...f, relatedFieldName: e.target.value }));
                                setShowRelatedFieldOptions(true);
                              }}
                              onFocus={() => setShowRelatedFieldOptions(true)}
                              onBlur={() => setTimeout(() => setShowRelatedFieldOptions(false), 150)}
                              className={`px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full ${formErrors.relatedFieldName ? 'border-red-300' : 'border-gray-300'}`}
                              placeholder="Buscar campo relacionado..."
                              autoComplete="off"
                              disabled={!formData.relatedFeatureName}
                            />
                            {showRelatedFieldOptions && formData.relatedFeatureName && (
                              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-auto">
                                {(fieldsByFeature[formData.relatedFeatureName] || [])
                                  .filter(field => field.toLowerCase().includes((formData.relatedFieldName || '').toLowerCase()))
                                  .map(field => (
                                    <div
                                      key={`related-field-${field}`}
                                      className="px-3 py-2 cursor-pointer hover:bg-blue-100"
                                      onMouseDown={() => {
                                        setFormData(prev => ({ ...prev, relatedFieldName: field }));
                                        setShowRelatedFieldOptions(false);
                                      }}
                                    >
                                      {field}
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                          {formErrors.relatedFieldName && <p className="mt-1 text-sm text-red-600">{formErrors.relatedFieldName}</p>}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Campo Valor - se muestra según operador */}
                {operatorsToShowValueInput.includes(formData.operator) && (
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Valor {operatorsToShowValueInput.includes(formData.operator) && '*'}
                        </label>
                        {formData.operator === 'data_type' ? (
                            <select
                                value={formData.value}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    value: e.target.value
                                })}
                                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.value ? 'border-red-300' : 'border-gray-300'}`}
                            >
                                <option value="">Seleccionar tipo de dato</option>
                                {dataTypesForSelect.map(dataType => (
                                    <option key={dataType} value={dataType.toLowerCase()}>{dataType}</option>
                                ))}
                            </select>
                        ) : (
                            <BaseInput
                                value={formData.value}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    value: e.target.value
                                })}
                                placeholder="Valor de referencia para la validación"
                                error={formErrors.value}
                            />
                        )}
                        {formErrors.value && (
                            <p className="mt-1 text-sm text-red-600">{formErrors.value}</p>
                        )}
                        {formData.operator !== 'data_type' && operatorValueHelpText[formData.operator] && (
                            <p className="mt-1 text-xs text-gray-500">
                                {operatorValueHelpText[formData.operator]}
                            </p>
                        )}
                    </div>
                )}

                {/* Sección CRS especial — solo para operador spatial_reference */}
                {formData.operator === 'spatial_reference' && (() => {
                  const crs = getSpatialRefInfo(formData.templateId, formData.featureName);
                  if (!crs) return null;
                  const rows = [
                    { label: 'Nombre CRS',    value: crs.crsName       || crs.CrsName },
                    { label: 'Nombre canónico', value: crs.canonicalName || crs.CanonicalName },
                    { label: 'WKT',           value: crs.wkt            || crs.Wkt },
                  ].filter(r => r.value);
                  if (!rows.length) return null;
                  return (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Referencia espacial esperada
                      </label>
                      <div className="bg-gray-50 rounded border border-gray-200 divide-y divide-gray-200">
                        {rows.map(({ label, value }) => (
                          <div key={label} className="flex items-center gap-2 px-2 py-1">
                            <span className="text-xs text-gray-500 w-32 shrink-0">{label}</span>
                            <span
                              className="text-xs font-mono text-gray-900 truncate"
                              title={value}
                            >
                              {value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Campo Dominio - se muestra para validaciones de dominio con operador 'in_domain' o 'range' */}
                {formData.type === 'domain' && (formData.operator === 'in_domain' || formData.operator === 'range') && (
                    <div className="mt-4 relative">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Dominio *
                        </label>
                        <select
                            value={formData.domain}
                            onChange={(e) => {
                                setFormData(prev => ({ ...prev, domain: e.target.value }));
                                // Ocultar tooltip inmediatamente cuando cambia la selección
                                setShowDomainTooltip(false);
                                setTooltipDomain(null);
                            }}
                            onMouseEnter={() => {
                                logger.debug('Mouse enter on select', { domain: formData.domain, availableDomainsCount: availableDomains.length });
                                // Solo mostrar tooltip si hay un dominio válido seleccionado
                                if (formData.domain && formData.domain.trim() !== '' && formData.domain !== 'domain_example_1' && formData.domain !== 'domain_example_2' && formData.domain !== 'domain_example_3' && formData.domain !== 'domain_example_4') {
                                    const selectedDomain = availableDomains.find(d => d.name === formData.domain);
                                    if (selectedDomain) {
                                        logger.debug('Showing tooltip for domain', { selectedDomain });
                                        setTooltipDomain(selectedDomain);
                                        setShowDomainTooltip(true);
                                    } else {
                                        logger.debug('Domain not found in availableDomains');
                                        setShowDomainTooltip(false);
                                        setTooltipDomain(null);
                                    }
                                } else {
                                    // Asegurar que se oculte si no hay dominio seleccionado
                                    logger.debug('Hiding tooltip - no valid domain selected');
                                    setShowDomainTooltip(false);
                                    setTooltipDomain(null);
                                }
                            }}
                            onMouseLeave={() => {
                                setShowDomainTooltip(false);
                                setTooltipDomain(null);
                            }}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.domain ? 'border-red-300' : 'border-gray-300'}`}
                        >
                            <option value="">Seleccionar dominio</option>
                            {availableDomains.length > 0 ? (
                                availableDomains
                                    .filter(domain => {
                                        // Filtrar dominios según el operador usando mapeo de tipos
                                        if (formData.operator === 'in_domain') {
                                            // Para "En Dominio": dominios con valores discretos (CodedValue)
                                            return domain.values && domain.values.length > 0;
                                        } else if (formData.operator === 'range') {
                                            // Para "Rango de Valores": dominios sin valores discretos (Range)
                                            return !domain.values || domain.values.length === 0;
                                        }
                                        return true; // Mostrar todos si no hay operador seleccionado
                                    })
                                    .map(domain => (
                                        <option key={domain.id} value={domain.name}>
                                            {domain.name} {domain.description ? `- ${domain.description}` : ''}
                                            {domain.type && ` (${domain.type})`}
                                        </option>
                                    ))
                            ) : (
                                <option value="" disabled>No hay dominios disponibles - seleccione plantilla, feature y campo primero</option>
                            )}
                        </select>

                        {/* Tooltip con valores del dominio */}
                        {showDomainTooltip && tooltipDomain && (
                            <div className="absolute z-50 mt-1 p-3 bg-gray-900 text-white text-sm rounded-md shadow-lg max-w-xs">
                                <div className="font-medium mb-1">{tooltipDomain.name}</div>
                                <div className="text-xs text-gray-300 mb-2">
                                    Tipo: {tooltipDomain.type || 'No especificado'}
                                </div>
                                {tooltipDomain.values && tooltipDomain.values.length > 0 ? (
                                    // Dominio con valores discretos (CodedValue)
                                    <>
                                        <div className="text-xs text-gray-300 mb-2">
                                            {tooltipDomain.values.length} valores permitidos:
                                        </div>
                                        <div className="max-h-32 overflow-y-auto">
                                            {tooltipDomain.values.map((value, index) => (
                                                <div key={index} className="text-xs">
                                                    • {value}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    // Dominio de rango
                                    <div className="text-xs text-gray-300">
                                        <div>Dominio de rango - valores continuos permitidos</div>
                                        {tooltipDomain.minValue !== undefined && tooltipDomain.maxValue !== undefined && (
                                            <div className="mt-1">
                                                Rango: {tooltipDomain.minValue} - {tooltipDomain.maxValue}
                                            </div>
                                        )}
                                        {tooltipDomain.description && (
                                            <div className="mt-1 text-xs text-gray-400">
                                                {tooltipDomain.description}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="absolute top-0 left-4 transform -translate-y-full">
                                    <div className="w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
                                </div>
                            </div>
                        )}

                        {formErrors.domain && (
                            <p className="mt-1 text-sm text-red-600">{formErrors.domain}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                            {availableDomains.length > 0
                                ? `Encontrados ${availableDomains.length} dominios en la GDB. Pase el mouse sobre un dominio seleccionado para ver sus valores.`
                                : 'Seleccionar el dominio contra el cual validar el campo'
                            }
                        </p>
                    </div>
                )}

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mensaje de validación fallida*
                  </label>
                  <textarea
                    value={formData.errorMessage}
                    onChange={(e) => setFormData({
                      ...formData,
                      errorMessage: e.target.value
                    })}
                    placeholder="Mensaje que se mostrará cuando falle la validación"
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.errorMessage ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.errorMessage && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.errorMessage}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Variables disponibles: {'{campo}'} = nombre del campo, {'{tabla}'} = nombre de la capa, {'{valor}'} = valor del registro
                  </p>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mensaje de validación exitosa*
                  </label>
                  <textarea
                    value={formData.successMessage}
                    onChange={(e) => setFormData({
                      ...formData,
                      successMessage: e.target.value
                    })}
                    placeholder="Mensaje que se mostrará cuando la validación sea exitosa"
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.successMessage ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.successMessage && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.successMessage}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Variables disponibles: {'{campo}'} = nombre del campo, {'{tabla}'} = nombre de la capa, {'{valor}'} = valor del registro
                  </p>
                </div>

              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
              <BaseButton
                variant="outline"
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
              >
                Cancelar
              </BaseButton>
              <BaseButton
                onClick={showCreateModal ? handleCreate : handleUpdate}
                className="flex items-center gap-2"
              >
                <Save size={16} />
                {showCreateModal ? 'Crear Validación' : 'Actualizar Validación'}
              </BaseButton>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ver Detalles */}
      {showViewModal && selectedValidation && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Detalles de la Validación
              </h3>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedValidation(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Información general */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nombre</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedValidation.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Estado</label>
                  <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    selectedValidation.isActive 
                      ? 'text-green-800 bg-green-100' 
                      : 'text-red-800 bg-red-100'
                  }`}>
                    {selectedValidation.isActive ? 'Activa' : 'Inactiva'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Descripción</label>
                <p className="mt-1 text-sm text-gray-900">{selectedValidation.description || 'Sin descripción'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tipo</label>
                  <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(selectedValidation.type)}`}>
                    {validationTypes.find(t => t.value === selectedValidation.type)?.label || selectedValidation.type}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Severidad</label>
                  <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(selectedValidation.severity)}`}>
                    {selectedValidation.severity.toUpperCase()}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Plantilla Asociada</label>
                <p className="mt-1 text-sm text-gray-900">
                  {(() => {
                    const template = templates.find(t => t.id === parseInt(selectedValidation.templateId));
                    return template ? template.name : 'Plantilla no encontrada';
                  })()}
                </p>
              </div>

              {/* Configuración de la regla */}
              <div className="border-t pt-4">
                <h4 className="text-md font-medium text-gray-900 mb-3">Configuración de la Regla</h4>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Capa (Feature)</label>
                    <p className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                      {selectedValidation.featureName}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Operador</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {operatorsByType[selectedValidation.type]?.find(op => op.value === selectedValidation.operator)?.label}
                    </p>
                  </div>
                </div>

                {['syntactic', 'domain', 'relational'].includes(selectedValidation.type) && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">Campo</label>
                    <p className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                      {selectedValidation.fieldName}
                    </p>
                  </div>
                )}

                {operatorsWithRelatedFeature.includes(selectedValidation.operator) && (
                  <div className={`grid gap-4 mt-4 ${['foreign_key', 'value_exists_in'].includes(selectedValidation.operator) ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Feature relacionada</label>
                      <p className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                        {selectedValidation.featureNameRelated}
                      </p>
                    </div>
                    {['foreign_key', 'value_exists_in'].includes(selectedValidation.operator) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Campo relacionado</label>
                        <p className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                          {selectedValidation.fieldNameRelated}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {operatorsToShowValueInput.includes(selectedValidation.operator) && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">Valor</label>
                    <p className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                      {selectedValidation.value}
                    </p>
                  </div>
                )}

                {selectedValidation.operator === 'spatial_reference' && (() => {
                  const crs = getSpatialRefInfo(selectedValidation.templateId, selectedValidation.featureName);
                  if (!crs) return null;
                  const rows = [
                    { label: 'Nombre CRS', value: crs.crsName },
                    { label: 'Nombre canónico', value: crs.canonicalName },
                    { label: 'WKT', value: crs.wkt },
                  ].filter(r => r.value);
                  if (!rows.length) return null;
                  return (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Referencia espacial esperada</label>
                      <div className="bg-gray-50 rounded border border-gray-200 divide-y divide-gray-200">
                        {rows.map(({ label, value }) => (
                          <div key={label} className="flex items-center gap-2 px-2 py-1">
                            <span className="text-xs text-gray-500 w-28 shrink-0">{label}</span>
                            <span
                              className="text-xs font-mono text-gray-900 truncate"
                              title={value}
                            >
                              {value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {selectedValidation.type === 'domain' && (selectedValidation.operator === 'in_domain' || selectedValidation.operator === 'range') && (
                  <>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700">Dominio</label>
                      <p className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                        {selectedValidation.domain}
                      </p>
                    </div>
                    {selectedValidation.subtype && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700">Subtipo</label>
                        <p className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                          {selectedValidation.subtype}
                        </p>
                      </div>
                    )}
                  </>
                )}

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">Mensaje de error</label>
                  <p className="mt-1 text-sm text-gray-900 bg-blue-50 px-3 py-2 rounded border-l-4 border-blue-400">
                    {selectedValidation.errorMessage}
                  </p>
                </div>

                {selectedValidation.successMessage && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">Mensaje de éxito</label>
                    <p className="mt-1 text-sm text-gray-900 bg-green-50 px-3 py-2 rounded border-l-4 border-green-400">
                      {selectedValidation.successMessage}
                    </p>
                  </div>
                )}
              </div>

              {/* Metadatos */}
              <div className="border-t pt-4">
                <h4 className="text-md font-medium text-gray-900 mb-3">Información de Seguimiento</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Creado por</label>
                    <p className="mt-1 text-gray-900">{selectedValidation.createdBy}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Fecha de creación</label>
                    <p className="mt-1 text-gray-900">{selectedValidation.createdAt}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Última modificación</label>
                    <p className="mt-1 text-gray-900">{selectedValidation.lastModified}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
              {hasPermission('validations', 'edit') && (
                <BaseButton
                  variant="outline"
                  onClick={() => {
                    setShowViewModal(false);
                    handleEdit(selectedValidation);
                  }}
                  className="flex items-center gap-2"
                >
                  <Edit2 size={16} />
                  Editar
                </BaseButton>
              )}
              <BaseButton
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedValidation(null);
                }}
              >
                Cerrar
              </BaseButton>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación */}
      {showDeleteModal && validationToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 text-center">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">
                Eliminar Validación
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  ¿Estás seguro de que quieres eliminar la validación <strong>{validationToDelete.name}</strong>?
                  Esta acción no se puede deshacer.
                </p>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <BaseButton
                variant="danger"
                onClick={confirmDelete}
                className="w-full sm:ml-3 sm:w-auto"
                disabled={loading}
              >
                {loading ? 'Eliminando...' : 'Eliminar'}
              </BaseButton>
              <BaseButton
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
                className="mt-3 w-full sm:mt-0 sm:w-auto"
              >
                Cancelar
              </BaseButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationRulesModule;